import { IAircraftGroup, IPosition } from "@vmosolutions/oc-interfaces";
import { IPairingStatusFilters, NegatableFilterKey, emptyPairingStatusFilters } from "./pairingStatusSelectors";
import { FLIGHT_PAIRING_STATUS_CATALOGUE, FlightPairingStatusCode } from "./pairingStatusTypes";

/**
 * Per-user persistence of the Flight Pairing Status window state: the last used filters and view are saved to
 * the user's profile (`appSettings.flightPairingStatus`, PUT /me/settings) and restored when the window opens.
 * The date range is deliberately not part of it.
 *
 * The selection is stored id-based: aircraft groups by id and positions by id + title, so it survives renames and
 * the client resolves ids against the current options when restoring. Anything unexpected in a stored value is
 * dropped rather than trusted.
 *
 * TODO: these two interfaces mirror backend-utils `database/user.ts`; move both into oc-interfaces next to
 * IUserAppSettings and delete the module augmentation below.
 */

export interface IFlightPairingStatusFilterSelection {
  search?: string;
  statuses?: string[];
  origins?: string[];
  destinations?: string[];
  flightTypeIds?: string[];
  aircraftGroupIds?: string[];
  positions?: { _id: string; title: string }[];
  includeSubAircraft?: boolean;
  problemsOnly?: boolean;
  negate?: { [filterKey: string]: boolean };
}

export type PairingStatusViewSetting = "calendar" | "list";

export interface IFlightPairingStatusUserSettings {
  lastFilters?: IFlightPairingStatusFilterSelection;
  view?: PairingStatusViewSetting;
  /** Epoch ms of the last save (client clock). */
  savedAt?: number;
}

declare module "@vmosolutions/oc-interfaces" {
  interface IUserAppSettings {
    flightPairingStatus?: IFlightPairingStatusUserSettings;
  }
}

const NEGATABLE_KEYS: NegatableFilterKey[] = [
  "statuses",
  "origins",
  "destinations",
  "flightTypeIds",
  "aircraftGroups",
  "positions",
];

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string" && v.length > 0) : [];

/** The serialisable, id-based form of the current filters. */
export const toFilterSelection = (filters: IPairingStatusFilters): IFlightPairingStatusFilterSelection => ({
  search: filters.search,
  statuses: [...filters.statuses],
  origins: [...filters.origins],
  destinations: [...filters.destinations],
  flightTypeIds: [...filters.flightTypeIds],
  aircraftGroupIds: filters.aircraftGroups.map((g) => g._id).filter((id): id is string => typeof id === "string"),
  positions: filters.positions.map((p) => ({ _id: p._id, title: p.title })),
  includeSubAircraft: filters.includeSubAircraft,
  problemsOnly: filters.problemsOnly,
  negate: NEGATABLE_KEYS.reduce(
    (acc, key) => (filters.negate?.[key] ? { ...acc, [key]: true } : acc),
    {} as { [filterKey: string]: boolean }
  ),
});

export interface IRestoredFilters {
  filters: IPairingStatusFilters;
  /** Aircraft group ids that could not be resolved yet (the groups load asynchronously). */
  pendingAircraftGroupIds: string[];
}

/**
 * Rebuild filters from a stored selection, validating every field and resolving ids against the current
 * options. Unknown statuses, positions and (once the groups are loaded) aircraft groups are dropped.
 */
export const fromFilterSelection = (
  selection: IFlightPairingStatusFilterSelection | null | undefined,
  options: { aircraftGroups?: IAircraftGroup[] | null; positions?: IPosition[] | null }
): IRestoredFilters => {
  if (!selection || typeof selection !== "object") {
    return { filters: emptyPairingStatusFilters, pendingAircraftGroupIds: [] };
  }

  const statuses = stringList(selection.statuses).filter(
    (code): code is FlightPairingStatusCode => code in FLIGHT_PAIRING_STATUS_CATALOGUE
  );

  const knownPositions = new Map((options.positions ?? []).map((p) => [p._id, p]));
  const positions = (Array.isArray(selection.positions) ? selection.positions : [])
    .filter((p): p is { _id: string; title: string } => !!p && typeof p._id === "string")
    .map((p) => {
      const current = knownPositions.get(p._id);
      return current ? { _id: current._id, title: current.title } : null;
    })
    .filter((p): p is { _id: string; title: string } => p !== null);

  const groupIds = stringList(selection.aircraftGroupIds);
  const loadedGroups = options.aircraftGroups ?? null;
  const aircraftGroups = loadedGroups ? loadedGroups.filter((g) => groupIds.includes(g._id)) : [];
  const pendingAircraftGroupIds = loadedGroups ? [] : groupIds;

  const negate: IPairingStatusFilters["negate"] = {};
  for (const key of NEGATABLE_KEYS) if (selection.negate?.[key] === true) negate[key] = true;

  return {
    filters: {
      search: typeof selection.search === "string" ? selection.search : "",
      statuses,
      origins: stringList(selection.origins),
      destinations: stringList(selection.destinations),
      flightTypeIds: stringList(selection.flightTypeIds),
      aircraftGroups,
      positions,
      includeSubAircraft: selection.includeSubAircraft === true,
      problemsOnly: selection.problemsOnly === true,
      negate,
    },
    pendingAircraftGroupIds,
  };
};

export const isViewSetting = (value: unknown): value is PairingStatusViewSetting =>
  value === "calendar" || value === "list";

/** Stable comparison of two selections (used to avoid saving what is already stored). */
export const sameSelection = (
  a: IFlightPairingStatusFilterSelection | null | undefined,
  b: IFlightPairingStatusFilterSelection | null | undefined
): boolean => JSON.stringify(normaliseSelection(a)) === JSON.stringify(normaliseSelection(b));

const normaliseSelection = (
  selection: IFlightPairingStatusFilterSelection | null | undefined
): IFlightPairingStatusFilterSelection => ({
  search: selection?.search ?? "",
  statuses: [...stringList(selection?.statuses)].sort(),
  origins: [...stringList(selection?.origins)].sort(),
  destinations: [...stringList(selection?.destinations)].sort(),
  flightTypeIds: [...stringList(selection?.flightTypeIds)].sort(),
  aircraftGroupIds: [...stringList(selection?.aircraftGroupIds)].sort(),
  positions: (Array.isArray(selection?.positions) ? selection.positions : [])
    .filter((p) => !!p && typeof p._id === "string")
    .map((p) => ({ _id: p._id, title: p.title }))
    .sort((x, y) => x._id.localeCompare(y._id)),
  includeSubAircraft: selection?.includeSubAircraft === true,
  problemsOnly: selection?.problemsOnly === true,
  negate: NEGATABLE_KEYS.reduce(
    (acc, key) => (selection?.negate?.[key] === true ? { ...acc, [key]: true } : acc),
    {} as { [filterKey: string]: boolean }
  ),
});
